<template>
    <v-expansion-panels class="rtls-expansion-panels" theme="primary">
        <v-expansion-panel class="rtls-expansion-panel" min-width="29.6rem">
            <v-expansion-panel-title class="rtls-expansion-panel-header" hide-actions>
                <template v-slot:default="{ expanded }">
                    <div>
                        <span class="rtls-expansion-panel-header__label">
                            {{ toSentenceCase($tkey('header')) }}
                        </span>
                        <icon v-if="isCompleted" icon-name="custom:empty-success-circle" small />
                    </div>
                    <main-expand-button :is-expanded="expanded" />
                </template>
            </v-expansion-panel-title>
            <v-expansion-panel-text class="rtls-expansion-panel-content">
                <div class="offer-restrictions">
                    <div class="offer-restrictions__group">
                        <div class="offer-restrictions__item offer-restrictions__item--checkbox">
                            <vuex-checkbox
                                :getter="maximumPerCustomerCheckboxGetter"
                                :setter="maximumPerCustomerCheckboxSetter"
                                :label="toSentenceCase($tkey('maxTriggerPerCustomer'))"
                                :disabled="isOfferMechanicReadOnly || isWeightPromotion"
                                class="checkbox"
                            />
                            <vuex-number-input
                                :vuex-module="vuexModule"
                                :tab="offer"
                                field-path="offerMechanic"
                                field-name="maximumPerCustomer"
                                height="21"
                                hide-details="auto"
                                :namespace="namespace"
                                :form-ref="formRef"
                                :need-pre-validation-on-set="true"
                                :validations="maxPerCustomerValidators"
                                class="offer-restrictions__number-narrow max-trigger-per-customer-input"
                                :disabled="
                                    isOfferMechanicReadOnly ||
                                        !maxPerCustomerEnabled ||
                                        isWeightPromotion
                                "
                                :reset-value-on="clearMaxPerCustomerEventName"
                            />
                        </div>
                        <div class="offer-restrictions__item offer-restrictions__item--checkbox">
                            <vuex-checkbox
                                :getter="maximumPerPurchaseCheckboxGetter"
                                :setter="maximumPerPurchaseCheckboxSetter"
                                :label="toSentenceCase($tkey('maxTriggerPerPurchase'))"
                                class="checkbox"
                                :disabled="isOfferMechanicReadOnly || isWeightPromotion"
                            />
                            <vuex-number-input
                                :vuex-module="vuexModule"
                                :tab="offer"
                                field-path="offerMechanic"
                                field-name="maximumPerPurchase"
                                height="21"
                                hide-details="auto"
                                :namespace="namespace"
                                :form-ref="formRef"
                                :need-pre-validation-on-set="true"
                                :validations="maxPerPurchaseValidators"
                                class="offer-restrictions__number-narrow max-trigger-per-customer-input"
                                :disabled="
                                    isOfferMechanicReadOnly ||
                                        !maxPerPurchaseEnabled ||
                                        isWeightPromotion
                                "
                                :reset-value-on="clearMaxPerPurchaseEventName"
                            />
                        </div>
                        <feature-toggle :toggle="showSingleTrip">
                            <v-divider />
                            <div class="offer-restrictions__stretch-offer">
                                <p>{{ toSentenceCase($tkey('storeVisitCondition')) }}:</p>
                                <vuex-radio-group
                                    :vuex-module="vuexModule"
                                    :tab="offer"
                                    field-path="offerMechanic"
                                    field-name="isSingleTripPromo"
                                    :namespace="namespace"
                                    :options="singleTripPromoOptions"
                                    :disabled="isOfferMechanicReadOnly"
                                    :inline="true"
                                    row
                                />
                            </div>
                        </feature-toggle>
                        <feature-toggle :toggle="allVariantsFlag">
                            <div
                                class="offer-restrictions__all-variants-flag offer-restrictions__item--checkbox"
                            >
                                <v-divider />
                                <vuex-checkbox
                                    :vuex-module="vuexModule"
                                    :tab="offer"
                                    field-path="additionalClientSpecificFields"
                                    field-name="allVariantsFlag"
                                    :namespace="namespace"
                                    :label="toSentenceCase($tkey('allVariants'))"
                                    :disabled="isOfferMechanicReadOnly"
                                />
                            </div>
                        </feature-toggle>
                        <feature-toggle :toggle="enableOfferCommercialField">
                            <v-divider />
                            <div class="offer-restrictions__commercial-field">
                                <div class="offer-restrictions__item--label">
                                    {{ toSentenceCase($tkey('commercial')) }}:
                                </div>
                                <div class="offer-restrictions__item">
                                    <vuex-select
                                        filled
                                        :placeholder="$tkey('commercial')"
                                        :multiple="false"
                                        :options="offerCommercialFieldsOptions"
                                        :vuex-module="vuexModule"
                                        :tab="offer"
                                        :width="ui.selectWidth"
                                        field-path="additionalClientSpecificFields"
                                        field-name="offerCommercialField"
                                        :namespace="namespace"
                                        clearable
                                        update-undefined-to-null
                                        :disabled="isOfferMechanicReadOnly"
                                        @change="onChangeCommercialField"
                                    />
                                </div>
                            </div>
                        </feature-toggle>
                    </div>
                    <div class="offer-restrictions__group offer-restrictions__group--right">
                        <div class="offer-restrictions__item--label">
                            {{ toSentenceCase($tkey('availability')) }}
                        </div>
                        <div class="offer-restrictions__item">
                            <vuex-select
                                filled
                                :placeholder="$tkey('to')"
                                :multiple="false"
                                :options="customerAvailabilityOptions"
                                :vuex-module="vuexModule"
                                :tab="offer"
                                :width="ui.selectWidth"
                                field-path="offerMechanic"
                                field-name="customerAvailability"
                                :namespace="namespace"
                                :disabled="isOfferMechanicReadOnly"
                            />
                        </div>
                        <div class="offer-restrictions__item">
                            <vuex-select
                                filled
                                :placeholder="$tkey('how')"
                                :multiple="false"
                                :options="promotionAvailabilityOptions"
                                :vuex-module="vuexModule"
                                :tab="offer"
                                :width="ui.selectWidth"
                                field-path="offerMechanic"
                                field-name="promotionAvailability"
                                :namespace="namespace"
                                :disabled="isOfferMechanicReadOnly"
                            />
                        </div>
                        <div class="offer-restrictions__item--label">
                            {{ toSentenceCase($tkey('withCoupon')) }}
                        </div>
                        <div class="offer-restrictions__item offer-restrictions__item--checkbox">
                            <template v-if="!isCouponLinkedWithCommercialField">
                                <vuex-checkbox
                                    :getter="couponEnabledCheckboxGetter"
                                    :setter="couponEnabledCheckboxSetter"
                                    class="checkbox coupon-barcode-input"
                                    :disabled="isOfferMechanicReadOnly"
                                />
                            </template>
                            <vuex-number-input
                                :vuex-module="vuexModule"
                                :tab="offer"
                                field-path="offerMechanic"
                                field-name="couponBarcode"
                                height="21"
                                hide-details="auto"
                                :namespace="namespace"
                                :validations="couponBarcodeValidators"
                                :disabled="isOfferMechanicReadOnly || couponDisabled"
                                :reset-value-on="clearBarcodeCouponEventName"
                                :form-ref="formRef"
                                :need-pre-validation-on-set="true"
                                :save-as-string="true"
                                format="numbers.default.justNumeric"
                                class="offer-restrictions__number-wide coupon-barcode-input"
                            />
                        </div>
                    </div>
                </div>
            </v-expansion-panel-text>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<script>
import { get, intersectionWith, isEmpty } from 'lodash';
import { mapGetters, mapState } from 'vuex';
import promotionAvailability from '@enums/promotion-availability';
import promotionTabs from '@enums/promotion-tabs';
import featureFlags from '@enums/feature-flags';
import vuexComponentMixin from '@/js/mixins/vuex-component';
import validators from '@/js/validators';
import { toSentenceCase } from '@/js/utils/string-utils';

const { offer } = promotionTabs;
const {
    showSingleTrip,
    allVariantsFlag,
    enableOfferCommercialField,
    couponLinkedWithCommercialField,
} = featureFlags;

export default {
    mixins: [vuexComponentMixin],
    localizationKey: 'planning.promotionsMaintenance.offer.offerMechanic.restrictions',

    props: {
        formRef: {
            type: Object,
            required: false,
        },
        isOfferMechanicReadOnly: {
            type: Boolean,
            default: false,
        },
    },

    data() {
        return {
            ui: {
                selectWidth: '16rem',
            },
            maxPerCustomerEnabled: false,
            maxPerPurchaseEnabled: false,
            couponEnabled: false,
            clearMaxPerCustomerEventName: 'clearMaxPerCustomer',
            clearMaxPerPurchaseEventName: 'clearMaxPerPurchase',
            clearBarcodeCouponEventName: 'clearBarcodeCoupon',
            showSingleTrip,
            allVariantsFlag,
            enableOfferCommercialField,
            offer,
        };
    },
    computed: {
        ...mapGetters('promotions', [
            'getCheckboxListOptions',
            'getStagingAreaField',
            'selectedPromotion',
            'getStagingAreaPromotionById',
        ]),
        ...mapState('clientConfig', {
            clientConfigValidations: state => state.validations,
        }),

        ...mapState('clientConfig', ['offerCommercialFields', 'toggleLogic']),

        promotion() {
            if (!isEmpty(this.selectedPromotion)) return this.selectedPromotion;

            // if creating a new promotion in the parkinglot, promotion will be in staging area and _id is default
            return this.getStagingAreaPromotionById('default');
        },

        isWeightPromotion() {
            return get(this.promotion, 'isWeightPromotion', false);
        },

        offerMechanic() {
            return this.getStagingAreaField({
                namespace: this.namespace,
                fieldName: 'offerMechanic',
            });
        },

        additionalClientSpecificFields() {
            return this.getStagingAreaField({
                namespace: this.namespace,
                fieldName: 'additionalClientSpecificFields',
            });
        },

        couponDisabled() {
            if (this.toggleLogic[couponLinkedWithCommercialField]) {
                return !this.commercialFieldHasValue;
            }

            return !this.couponEnabled;
        },

        // This is a computed because these validation rules vary depending on whether or not the checkbox is checked.
        maxPerCustomerValidators() {
            return this.maxPerCustomerEnabled
                ? [
                      {
                          validator: validators.minValue,
                          params: [1],
                      },
                      {
                          validator: validators.maxValue,
                          params: [
                              this.clientConfigValidations.offerMechanicMaxPerCustomerCount.params
                                  .value,
                          ],
                      },
                  ]
                : [];
        },

        // This is a computed because these validation rules vary depending on whether or not the checkbox is checked.
        maxPerPurchaseValidators() {
            return this.maxPerPurchaseEnabled
                ? [
                      {
                          validator: validators.minValue,
                          params: [1],
                      },
                      {
                          validator: validators.maxValue,
                          params: [
                              this.clientConfigValidations.offerMechanicMaxPerPurchaseCount.params
                                  .value,
                          ],
                      },
                  ]
                : [];
        },
        coupuonCodeHasValue() {
            return this.offerMechanic.couponBarcode && this.offerMechanic.couponBarcode !== '-';
        },
        couponBarcodeValidators() {
            return !this.couponDisabled && this.coupuonCodeHasValue
                ? [
                      {
                          validator: validators.numericString,
                      },
                      {
                          validator: validators.maxLength,
                          params: [14],
                      },
                  ]
                : [];
        },

        customerAvailabilityOptions() {
            // The admin preset sandbox has no planning context, so every customer group is allowed.
            const scenario = this.context?.selectedScenario;
            const scenarioId = this.context?.scenarioId;
            const allowedCustomerGroupsForScenario = scenario?.customerGroups;
            const allOptionsAvailableForScenario = this.getCheckboxListOptions({
                attributeName: 'customerGroups',
                attributeKey: 'key',
                resource: 'clientConfig',
                getOptionsFunction: 'getCustomerRestrictions',
                scenarioId,
                useSelectedContext: !!this.context,
            });
            const allowedOptionsForScenario =
                scenarioId || (scenario && scenario._id)
                    ? intersectionWith(
                          allOptionsAvailableForScenario,
                          allowedCustomerGroupsForScenario,
                          (available, allowed) => {
                              return available.reference.key === allowed.key;
                          }
                      )
                    : [...allOptionsAvailableForScenario];

            return allowedOptionsForScenario.map(option => {
                return {
                    text: toSentenceCase(option.reference.description),
                    value: option.reference.key,
                };
            });
        },

        promotionAvailabilityOptions() {
            return [
                {
                    text: toSentenceCase(this.$tkey('promotionAvailabilities.onPurchase')),
                    value: promotionAvailability.onPurchase,
                },
                {
                    text: toSentenceCase(this.$tkey('promotionAvailabilities.handInCoupon')),
                    value: promotionAvailability.handInCoupon,
                },
            ];
        },

        isCompleted() {
            return (
                !!this.offerMechanic.maximumPerCustomer ||
                !!this.offerMechanic.maximumPerPurchase ||
                !!this.offerMechanic.couponBarcode ||
                !!this.offerMechanic.promotionAvailability ||
                (this.offerMechanic.customerAvailability &&
                    this.offerMechanic.customerAvailability !== 'allCustomers')
            );
        },
        singleTripPromoOptions() {
            return [
                {
                    value: true,
                    text: toSentenceCase(this.$tkey('single')),
                },
                {
                    value: false,
                    text: toSentenceCase(this.$tkey('multiple')),
                },
            ];
        },

        offerCommercialFieldsOptions() {
            return this.offerCommercialFields.map(ocf => {
                let isNumberValue = false;
                try {
                    if (!Number.isNaN(parseFloat(ocf.field))) {
                        isNumberValue = true;
                    }
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error('Error in offerCommercialFieldsOptions', e);
                }
                return {
                    text: ocf.translationKey
                        ? toSentenceCase(this.$t(ocf.translationKey))
                        : ocf.title,
                    value: isNumberValue ? Number(ocf.field) : ocf.field,
                };
            });
        },
        isCouponLinkedWithCommercialField() {
            return this.toggleLogic[couponLinkedWithCommercialField];
        },
        commercialFieldHasValue() {
            return !!this.additionalClientSpecificFields?.offerCommercialField;
        },
    },

    watch: {
        offerMechanic() {
            this.initCheckboxes();
        },
    },

    async mounted() {
        this.initCheckboxes();
    },

    methods: {
        initCheckboxes() {
            // set up checkboxes for triggers
            this.maxPerCustomerEnabled = !!this.offerMechanic.maximumPerCustomer;
            this.maxPerPurchaseEnabled = !!this.offerMechanic.maximumPerPurchase;
            if (!this.toggleLogic[couponLinkedWithCommercialField]) {
                this.couponEnabled = !!this.offerMechanic.couponBarcode;
            }
        },

        maximumPerCustomerCheckboxGetter() {
            return this.maxPerCustomerEnabled;
        },

        maximumPerCustomerCheckboxSetter(value) {
            this.maxPerCustomerEnabled = value;
            if (!value) {
                // Reset the maximum quantity if no limit is required.
                this.emitter.emit(this.clearMaxPerCustomerEventName);
            }
        },

        maximumPerPurchaseCheckboxGetter() {
            return this.maxPerPurchaseEnabled;
        },

        maximumPerPurchaseCheckboxSetter(value) {
            this.maxPerPurchaseEnabled = value;
            if (!value) {
                // Reset the maximum quantity if no limit is required.
                this.emitter.emit(this.clearMaxPerPurchaseEventName);
            }
        },

        couponEnabledCheckboxGetter() {
            return this.couponEnabled;
        },

        couponEnabledCheckboxSetter(value) {
            this.couponEnabled = value;
            if (!value) {
                // Reset the value if required.
                this.emitter.emit(this.clearBarcodeCouponEventName);
            }
        },
        onChangeCommercialField(value) {
            if (!value && this.toggleLogic[couponLinkedWithCommercialField]) {
                // Reset the value if required.
                this.emitter.emit(this.clearBarcodeCouponEventName);
            }
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@style/base/_mixins.scss';
@import '@style/base/_variables.scss';

.rtls-expansion-panel {
    .rtls-expansion-panel-header {
        padding: 0;
        > * {
            flex-grow: 0;
        }

        &__label {
            color: $promo-primary-colour;
            font-size: 1.2rem;
            letter-spacing: 0;
            padding-right: 1rem;
        }
    }
}

.offer-restrictions {
    @include flex-row;
    gap: 1.6rem;

    &__number-narrow {
        width: 5rem;
        padding-left: 1rem;
    }

    &__number-wide {
        max-width: 13rem;
        flex: 1;
    }

    &__group {
        &--right {
            padding-left: 2rem;
            border-left: 0.1rem solid $promo-grey-4;
        }
    }

    &__item {
        padding-bottom: 1.2rem;
        display: flex;
        justify-content: space-between;
        align-items: center;

        &--label {
            padding-bottom: 0.5rem;
        }

        &--checkbox {
            margin-left: -0.8rem;
        }
    }

    &__stretch-offer {
        p {
            padding: 0.3rem 0;
        }

        :deep(.v-input--selection-controls) {
            margin-top: 0;
        }

        :deep(legend.v-label) {
            padding-right: 0;
        }
    }

    &__all-variants-flag {
        padding-top: 0.6rem;
        padding-bottom: 1rem;

        > div {
            margin-top: 0.5rem;
        }
    }

    &__commercial-field {
        div.offer-restrictions__item--label {
            padding-top: 0.3rem;
            padding-bottom: 0.5rem;
        }

        :deep(.v-input--selection-controls) {
            margin-top: 0;
        }
    }
}

:deep(.v-expansion-panel-header__icon .v-icon) {
    font-size: 2.8rem;
    color: $rtls-primary-colour !important;
}

:deep(.v-expansion-panel--active) {
    .v-expansion-panel-title--active
        .v-expansion-panel-header__icon:not(.v-expansion-panel-header__icon--disable-rotate)
        .v-icon {
        color: $promo-light-blue !important;
    }
    .v-expansion-panel-title {
        min-height: 4rem;
    }
}

:deep(.v-expansion-panel-title) {
    min-height: 4rem;
}
</style>
